import { useEffect, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring, useScroll } from "framer-motion";
import Lenis from "lenis";
import { IMG } from "@/lib/content";
import { useSeason } from "@/lib/holiday";
import { SantaHat, LoaderSnow } from "@/components/fx/Holiday";

export const SmoothScroll = () => {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let id;
    const raf = (t) => { lenis.raf(t); id = requestAnimationFrame(raf); };
    id = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(id); lenis.destroy(); };
  }, []);
  return null;
};

export const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 25 });
  return <motion.div data-testid="scroll-progress" style={{ scaleX }} className="fixed top-0 left-0 right-0 h-[2px] bg-gold origin-left z-[60]" />;
};

export const Cursor = () => {
  const x = useSpring(useMotionValue(-100), { stiffness: 500, damping: 40 });
  const y = useSpring(useMotionValue(-100), { stiffness: 500, damping: 40 });
  const [hover, setHover] = useState(false);
  const [enabled] = useState(() => window.matchMedia("(pointer: fine)").matches);
  useEffect(() => {
    if (!enabled) return;
    const move = (e) => {
      x.set(e.clientX); y.set(e.clientY);
      setHover(!!e.target.closest?.("a,button,[role=button],input,select"));
    };
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, [enabled, x, y]);
  if (!enabled) return null;
  return (
    <motion.div style={{ x, y }} className="fixed top-0 left-0 z-[100] pointer-events-none mix-blend-difference hidden lg:block">
      <motion.div animate={{ width: hover ? 56 : 14, height: hover ? 56 : 14, opacity: hover ? 0.9 : 1 }} transition={{ type: "spring", stiffness: 300, damping: 22 }}
        className="-translate-x-1/2 -translate-y-1/2 rounded-full border border-[#e3cb94] bg-[#e3cb94]/30" />
    </motion.div>
  );
};

export const Intro = ({ ready }) => {
  const [show, setShow] = useState(() => !sessionStorage.getItem("ajs_intro"));
  const { season } = useSeason();
  useEffect(() => {
    if (!show || !ready) return;
    const t = setTimeout(() => { setShow(false); sessionStorage.setItem("ajs_intro", "1"); }, 1900);
    return () => clearTimeout(t);
  }, [show, ready]);
  return (
    <AnimatePresence>
      {(show || !ready) && (
        <motion.div data-testid="intro-curtain" className="fixed inset-0 z-[90] bg-[#0b0a08] flex flex-col items-center justify-center"
          exit={{ clipPath: "inset(0 0 100% 0)" }} initial={{ clipPath: "inset(0 0 0% 0)" }} transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}>
          {season && <LoaderSnow />}
          <div className="relative h-28 w-28">
            <motion.img src={IMG.logo} alt="AJS Luxury" className="h-full w-full object-contain" initial={{ opacity: 0, scale: 0.8, filter: "blur(10px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }} />
            {season && <SantaHat testId="intro-santa-hat" delay={0.9} />}
          </div>
          <motion.div className="h-px bg-gold mt-8" initial={{ width: 0 }} animate={{ width: 160 }} transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.2 }} />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const PageTransition = ({ children, id }) => (
  <motion.div key={id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
    {children}
  </motion.div>
);
